<template>
    <div class="grid">
        <Toast/>
        <div class="col-12" style="max-width: 1100px;">
            <div class="card">
                <h4>Plan de cuentas</h4>
                <p style="max-width: 720px;">
                    La estructura sobre la que se registran tus asientos. Las cuentas en
                    <b>negrita</b> son grupos: no reciben movimientos, solo agrupan a las de abajo.
                    Tus cajas, bancos y tarjetas aparecen como una cuenta propia cada una, para
                    poder conciliarlas por separado.
                </p>
                <p style="max-width: 720px; color: #555;">
                    Puedes <b>agregar</b> tus propias cuentas dentro de un grupo, <b>renombrarlas</b>
                    y <b>desactivar</b> las que no uses. Una cuenta nueva hereda la naturaleza
                    (activo, gasto, ingreso…) del grupo donde la crees. Las cuentas
                    <i>del sistema</i> se pueden renombrar, pero no desactivar: son las que el
                    programa usa para registrar los asientos automáticos.
                </p>

                <div style="display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
                    <span class="p-input-icon-left">
                        <i class="pi pi-search"/>
                        <InputText v-model="filter" placeholder="Buscar cuenta…" style="width: 260px;"/>
                    </span>
                    <Button :label="onlyPostable ? 'Mostrar grupos' : 'Solo cuentas de movimiento'"
                            :icon="onlyPostable ? 'pi pi-sitemap' : 'pi pi-list'"
                            class="p-button-text p-button-sm" @click="onlyPostable = !onlyPostable"/>
                    <Button label="Nueva cuenta" icon="pi pi-plus" class="p-button-sm"
                            @click="openCreate(null)"/>
                    <Button label="Ir a Mapeos" icon="pi pi-link" class="p-button-text p-button-sm"
                            @click="$router.push('/dashboard/contabilidad/mapeos')"/>
                </div>

                <DataTable :value="visible" :loading="loading" class="mt-2"
                           :scrollable="true" scrollHeight="560px">
                    <Column header="Código" style="width: 130px;">
                        <template #body="slotProps">
                            <small style="color: #777;">{{ slotProps.data.code }}</small>
                        </template>
                    </Column>
                    <Column header="Cuenta" style="min-width: 280px;">
                        <template #body="slotProps">
                            <span :style="rowStyle(slotProps.data)">{{ slotProps.data.name }}</span>
                            <Tag v-if="slotProps.data.finantialAccount" class="ml-2" severity="info"
                                 :value="moneyTypeLabel(slotProps.data.finantialAccount.moneyType)"/>
                            <i v-if="!slotProps.data.active" class="pi pi-eye-slash ml-2"
                               style="color:#999; font-size:0.8rem;" v-tooltip="'Cuenta inactiva'"></i>
                        </template>
                    </Column>
                    <Column header="Naturaleza" style="width: 150px;">
                        <template #body="slotProps">
                            <span style="color:#777;">{{ typeLabel(slotProps.data.type) }}</span>
                        </template>
                    </Column>
                    <Column header="Rol" style="width: 170px;">
                        <template #body="slotProps">
                            <small v-if="slotProps.data.finantialAccount" style="color:#555;">
                                {{ slotProps.data.finantialAccount.name }}
                            </small>
                            <small v-else-if="!slotProps.data.isPostable" style="color:#999;">Grupo</small>
                            <small v-else-if="slotProps.data.builtinKey" style="color:#999;">Del sistema</small>
                        </template>
                    </Column>
                    <Column header="" style="width: 140px;">
                        <template #body="slotProps">
                            <Button v-if="slotProps.data.canAddChild" icon="pi pi-plus"
                                    class="p-button-text p-button-sm" v-tooltip="'Agregar una cuenta aquí dentro'"
                                    @click="openCreate(slotProps.data)"/>
                            <Button v-if="slotProps.data.canRename" icon="pi pi-pencil"
                                    class="p-button-text p-button-sm" v-tooltip="'Renombrar'"
                                    @click="openRename(slotProps.data)"/>
                            <Button v-if="slotProps.data.canDeactivate" icon="pi pi-eye-slash"
                                    class="p-button-text p-button-sm p-button-secondary"
                                    v-tooltip="'Desactivar'" @click="openToggle(slotProps.data)"/>
                            <Button v-if="slotProps.data.canActivate" icon="pi pi-eye"
                                    class="p-button-text p-button-sm" v-tooltip="'Activar'"
                                    @click="toggle(slotProps.data, true)"/>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <!-- ===== Nueva cuenta ===== -->
        <Dialog v-model:visible="createDialog" header="Nueva cuenta" :modal="true"
                :style="{width: '520px'}">
            <div style="display:flex; flex-direction:column; gap:0.9rem;">
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">¿Dentro de qué grupo va?</label>
                    <Dropdown v-model="form.parentId" :options="parentOptions" optionLabel="label"
                              optionValue="id" :filter="true" filterPlaceholder="Buscar…"
                              placeholder="Elige el grupo" style="width:100%;"/>
                    <small style="color:#777;">
                        La cuenta hereda su naturaleza: si la creas dentro de Gastos operativos,
                        será una cuenta de gasto.
                    </small>
                </div>
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">Nombre</label>
                    <InputText v-model="form.name" placeholder="Uniformes, Comisiones…"
                               style="width:100%;" @keyup.enter="createAccount"/>
                </div>
                <Message v-if="parentIsLeaf" severity="info" :closable="false">
                    «{{ parentName }}» pasará a ser un grupo: dejará de recibir movimientos
                    y estos irán a las cuentas que le cuelgues.
                </Message>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="createDialog = false"/>
                <Button label="Crear" icon="pi pi-check" :loading="working" @click="createAccount"/>
            </template>
        </Dialog>

        <!-- ===== Renombrar ===== -->
        <Dialog v-model:visible="renameDialog" header="Renombrar cuenta" :modal="true"
                :style="{width: '460px'}">
            <div style="display:flex; flex-direction:column; gap:0.9rem;">
                <p style="margin:0; color:#555;">
                    Solo cambia el nombre que ves: el código y los asientos ya registrados
                    quedan igual.
                </p>
                <div>
                    <label style="display:block; font-size:0.85rem; color:#777;">Nombre</label>
                    <InputText v-model="form.name" style="width:100%;" @keyup.enter="renameAccount"/>
                </div>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="renameDialog = false"/>
                <Button label="Guardar" icon="pi pi-check" :loading="working" @click="renameAccount"/>
            </template>
        </Dialog>

        <!-- ===== Desactivar ===== -->
        <Dialog v-model:visible="toggleDialog" :header="'Desactivar: ' + (current.name || '')"
                :modal="true" :style="{width: '460px'}">
            <p style="margin:0; color:#555;">
                Dejará de ofrecerse para registrar movimientos y para los mapeos. Lo que ya
                tiene registrado sigue apareciendo en tus reportes, y puedes volver a activarla
                cuando quieras.
            </p>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="toggleDialog = false"/>
                <Button label="Desactivar" icon="pi pi-eye-slash" class="p-button-secondary"
                        :loading="working" @click="toggle(current, false)"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';

export default {
    data() {
        return {
            loading: true,
            working: false,
            accounts: [],
            filter: '',
            onlyPostable: false,
            createDialog: false,
            renameDialog: false,
            toggleDialog: false,
            current: {},
            form: {parentId: null, name: ''},
        };
    },
    computed: {
        visible() {
            const text = this.filter.trim().toLowerCase();
            return this.accounts.filter(account => {
                if (this.onlyPostable && !account.isPostable) return false;
                if (!text) return true;
                return account.name.toLowerCase().includes(text)
                    || account.code.toLowerCase().includes(text);
            });
        },
        // Dónde se puede colgar una cuenta nueva. Lo decide el backend (canAddChild):
        // quedan fuera la tesorería —esas cuentas se crean solas por cada caja/banco—
        // y las cuentas con movimientos, que no pueden volverse grupo.
        parentOptions() {
            return this.accounts
                .filter(account => account.canAddChild)
                .map(account => ({
                    id: account.id,
                    // Espacios duros: los normales se colapsan en el desplegable y
                    // se pierde la jerarquía, que es lo que indica dónde va a caer.
                    label: '\u00a0\u00a0'.repeat(account.level || 0)
                        + account.code + '\u00a0\u00a0' + account.name,
                }));
        },
        parent() {
            return this.accounts.find(account => account.id === this.form.parentId) || null;
        },
        parentName() {
            return this.parent ? this.parent.name : '';
        },
        parentIsLeaf() {
            return !!(this.parent && this.parent.isPostable);
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            const response = await httpRequest.getAccountingChart();
            this.loading = false;
            if (response.status === 200) {
                this.accounts = response.data.accounts;
            } else {
                this.$toast.add({severity: 'error', summary: 'Error',
                                 detail: 'No se pudo cargar el plan de cuentas', life: 4000});
            }
        },
        openCreate(row) {
            this.form = {parentId: row ? row.id : null, name: ''};
            this.createDialog = true;
        },
        openRename(row) {
            this.current = row;
            this.form = {parentId: null, name: row.name};
            this.renameDialog = true;
        },
        openToggle(row) {
            this.current = row;
            this.toggleDialog = true;
        },
        async createAccount() {
            if (!this.form.parentId || !this.form.name.trim()) {
                this.$toast.add({severity: 'warn', summary: 'Faltan datos',
                                 detail: 'Elige el grupo y escribe el nombre', life: 4000});
                return;
            }
            this.working = true;
            const response = await httpRequest.createAccountingAccount(
                this.form.parentId, this.form.name);
            this.working = false;
            if (response.status === 200) {
                this.createDialog = false;
                this.$toast.add({severity: 'success', summary: 'Cuenta creada',
                                 detail: response.data.account.name, life: 3000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo crear',
                                 detail: response.error || '', life: 6000});
            }
        },
        async renameAccount() {
            this.working = true;
            const response = await httpRequest.renameAccountingAccount(
                this.current.id, this.form.name);
            this.working = false;
            if (response.status === 200) {
                this.renameDialog = false;
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo renombrar',
                                 detail: response.error || '', life: 6000});
            }
        },
        async toggle(row, active) {
            this.working = true;
            const response = await httpRequest.setAccountingAccountActive(row.id, active);
            this.working = false;
            this.toggleDialog = false;
            if (response.status === 200) {
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo cambiar',
                                 detail: response.error || '', life: 6000});
            }
        },
        rowStyle(row) {
            const indent = (row.level || 0) * 14;
            return 'padding-left:' + indent + 'px;' + (row.isPostable ? '' : ' font-weight:700;');
        },
        typeLabel(type) {
            return {ASSET: 'Activo', LIABILITY: 'Pasivo', EQUITY: 'Patrimonio',
                    INCOME: 'Ingreso', EXPENSE: 'Gasto'}[type] || type;
        },
        moneyTypeLabel(moneyType) {
            return {CASH: 'Efectivo', TRANSFER: 'Banco', CARD: 'Tarjeta'}[moneyType] || moneyType;
        },
    },
}
</script>
